import { useQuery } from "@tanstack/react-query";
import { Badge } from "@veil/ui/components/badge";
import { VeilLogo } from "@veil/ui/components/brand";
import { Button } from "@veil/ui/components/button";
import {
	DashboardShell,
	type NavLinkComponent,
	SiteHeader,
} from "@veil/ui/layouts";
import Activity from "lucide-react/dist/esm/icons/activity";
import AlertCircle from "lucide-react/dist/esm/icons/circle-alert";
import { forwardRef, type ReactNode, useState } from "react";
import { Link, useLocation } from "wouter";
import { CommandPalette } from "@/components/command-palette";
import { ErrorBoundary } from "@/components/error-boundary";
import { OperationsDock } from "@/components/operations-dock";
import { api, unwrap } from "@/lib/api";
import { hasRole, useAuth } from "@/lib/auth-context";
import { NAV_SECTIONS, ROUTE_LABELS } from "@/lib/nav";
import {
	useActiveOperations,
	useOperationCompletionToasts,
} from "@/lib/operations/use-active-operations";
import { ShellPathnameContext } from "@/lib/shell-chrome";
import { useSystemVersion } from "@/lib/system-version";
import { useSelfUpdateCheck } from "@/lib/updates/hooks";

interface ClusterIndicatorResponse {
	success: boolean;
	stats?: { dirty: boolean };
}

// wouter's Link satisfies the shell's router-agnostic link contract.
const WouterNavLink: NavLinkComponent = forwardRef<
	HTMLAnchorElement,
	{
		href: string;
		className?: string;
		children: ReactNode;
		onClick?: () => void;
		title?: string;
	}
>(({ href, ...props }, ref) => <Link ref={ref} href={href} {...props} />);
WouterNavLink.displayName = "WouterNavLink";

export function Layout({ children }: { children: ReactNode }) {
	const { user, logout } = useAuth();
	const [location] = useLocation();

	// The sidebar and the breadcrumb read this rather than the location, so a
	// page that isn't at the address the browser is showing (the catch-all) can
	// stop the chrome from claiming otherwise. See lib/shell-chrome.ts.
	const [shellPathnameOverride, setShellPathnameOverride] = useState<
		string | null
	>(null);
	const shellPathname = shellPathnameOverride ?? location;

	// Lightweight query just for the sidebar + header indicators. Refetches on
	// the same 30s cadence as the Cluster page so the dot drops as soon as a
	// render settles. Soft-fails (no dot) if the endpoint errors.
	const clusterStats = useQuery({
		queryKey: ["cluster"],
		queryFn: () =>
			(
				unwrap(api.api.cluster.get()) as Promise<ClusterIndicatorResponse>
			).catch((): ClusterIndicatorResponse => ({ success: false })),
		enabled: !!user,
		refetchInterval: 30_000,
		staleTime: 15_000,
	});
	const clusterDirty = clusterStats.data?.stats?.dirty === true;

	const v = useSystemVersion().data;
	const isDevBuild = v?.source === "package-json";

	// Quiet Portainer-style indicator: a small badge next to the version
	// when a newer cluster-manager image is published. Long staleTime — the
	// registry check only changes when a release ships; the Updates page
	// owns the explicit "Check now".
	const selfUpdate = useSelfUpdateCheck({ enabled: !!user });
	const updateAvailable = selfUpdate.data?.check.updateAvailable === true;

	// Mounted once, here, so a finished operation announces a single toast
	// no matter how many components subscribe to the running list.
	useOperationCompletionToasts();
	const runningCount = useActiveOperations().data?.length ?? 0;

	const brandSubtitle = (
		<span className="flex items-center gap-1 font-mono">
			{v ? `v${v.version}` : "…"}
			{isDevBuild ? (
				<Badge
					variant="outline"
					className="px-1 py-0 text-[9px] uppercase tracking-wider"
				>
					dev
				</Badge>
			) : null}
			{updateAvailable ? (
				<Badge
					variant="warning"
					asChild
					className="px-1 py-0 text-[9px] uppercase tracking-wider"
				>
					<Link
						href="/updates"
						title={`Update available: ${selfUpdate.data?.check.latest ?? ""}`}
					>
						Update
					</Link>
				</Badge>
			) : null}
		</span>
	);

	// Role-gate the Admin section by hiding its items for non-admins.
	const navSections = NAV_SECTIONS.map((section) =>
		section.label === "Admin"
			? {
					...section,
					items: section.items.map((item) => ({
						...item,
						hidden: !hasRole(user, "admin"),
					})),
				}
			: section,
	);

	return (
		<DashboardShell
			navSections={navSections}
			pathname={shellPathname}
			LinkComponent={WouterNavLink}
			brand={{
				name: "Cluster Manager",
				icon: <VeilLogo className="size-5" />,
				subtitle: brandSubtitle,
			}}
			user={
				user
					? {
							name: user.username,
							subtitle: user.role,
							initials: user.username.slice(0, 2).toUpperCase(),
						}
					: undefined
			}
			onLogout={() => void logout()}
			indicatorActive={clusterDirty}
			header={
				<SiteHeader
					pathname={shellPathname}
					LinkComponent={WouterNavLink}
					routeLabels={ROUTE_LABELS}
					actions={
						<>
							{clusterDirty ? (
								<Badge
									variant="warning"
									className="hidden gap-1 sm:inline-flex"
									title="Unrendered cluster changes"
								>
									<AlertCircle className="size-3" />
									Cluster dirty
								</Badge>
							) : null}
							<CommandPalette />
							<Button
								variant="ghost"
								size="icon-sm"
								asChild
								aria-label={
									runningCount > 0
										? `Operations, ${runningCount} running`
										: "Operations"
								}
								title="Recent operations"
							>
								<Link href="/operations" className="relative">
									<Activity />
									{/* Ternary, not &&: a count of 0 would render a
									    literal "0" over the icon. */}
									{runningCount > 0 ? (
										<span
											aria-hidden
											className="absolute -top-0.5 -right-0.5 flex size-3.5 items-center justify-center rounded-full bg-amber-500 font-mono text-[9px] leading-none text-white tabular-nums dark:text-amber-950"
										>
											{runningCount > 9 ? "9+" : runningCount}
										</span>
									) : null}
								</Link>
							</Button>
						</>
					}
				/>
			}
		>
			<ErrorBoundary key={location} scope="Page">
				<ShellPathnameContext.Provider value={setShellPathnameOverride}>
					{children}
				</ShellPathnameContext.Provider>
			</ErrorBoundary>
			<OperationsDock />
		</DashboardShell>
	);
}
